// hooks/useTransferDestinations.js
import { useState, useCallback, useRef } from "react"
import {
  useLazyGetTransLocationsFromQuery,
  useLazyGetTransLocationsToQuery,
  useLazySearchTransLocationsFromQuery,
  useLazySearchTransLocationsToQuery,
} from "../../app/services/transfer/search"

const useTransferDestinations = () => {
  const [selectedFrom, setSelectedFrom] = useState(null)
  const [selectedTo, setSelectedTo] = useState(null)
  const [isSearchingFrom, setIsSearchingFrom] = useState(false)
  const [isSearchingTo, setIsSearchingTo] = useState(false)

  // Запоминаем последний поисковый запрос для каждого поля
  const lastQueryRef = useRef({ from: "", to: "" })

  const [triggerDefaultFrom, { data: defaultFromData, isLoading: isLoadingDefaultFrom }] =
    useLazyGetTransLocationsFromQuery()

  const [triggerDefaultTo, { data: defaultToData, isLoading: isLoadingDefaultTo }] =
    useLazyGetTransLocationsToQuery()

  const [triggerSearchFrom, { data: searchFromData, isLoading: isLoadingSearchFrom }] =
    useLazySearchTransLocationsFromQuery()

  const [triggerSearchTo, { data: searchToData, isLoading: isLoadingSearchTo }] =
    useLazySearchTransLocationsToQuery()

  const departureTerminals =
    (isSearchingFrom ? searchFromData : defaultFromData)?.destinations ?? []

  const arrivalTerminals = !selectedFrom
    ? []
    : ((isSearchingTo ? searchToData : defaultToData)?.destinations ?? [])

  const fetchDefault = useCallback(
    (field) => {
      if (field === "from") {
        const lastQuery = lastQueryRef.current.from
        // Если был поисковый запрос — повторяем его, не сбрасываем на дефолт
        if (lastQuery && lastQuery.trim().length >= 2) {
          setIsSearchingFrom(true)
          triggerSearchFrom({ q: lastQuery })
        } else {
          setIsSearchingFrom(false)
          triggerDefaultFrom()
        }
      } else {
        if (!selectedFrom) return
        const lastQuery = lastQueryRef.current.to
        if (lastQuery && lastQuery.trim().length >= 2) {
          setIsSearchingTo(true)
          triggerSearchTo({
            q: lastQuery,
            from_type: selectedFrom.type,
            from_id: selectedFrom.id,
          })
        } else {
          setIsSearchingTo(false)
          triggerDefaultTo({
            from_type: selectedFrom.type,
            from_id: selectedFrom.id,
          })
        }
      }
    },
    [selectedFrom, triggerDefaultFrom, triggerDefaultTo, triggerSearchFrom, triggerSearchTo]
  )

  const fetchSearch = useCallback(
    (query, field) => {
      if (!query || query.trim().length < 2) {
        // Сбрасываем запомненный запрос и идём на дефолт
        lastQueryRef.current[field] = ""
        fetchDefault(field)
        return
      }

      // Запоминаем запрос
      lastQueryRef.current[field] = query

      if (field === "from") {
        setIsSearchingFrom(true)
        triggerSearchFrom({ q: query })
      } else {
        if (!selectedFrom) return
        setIsSearchingTo(true)
        triggerSearchTo({
          q: query,
          from_type: selectedFrom.type,
          from_id: selectedFrom.id,
        })
      }
    },
    [selectedFrom, triggerSearchFrom, triggerSearchTo, fetchDefault]
  )

  const selectDestination = useCallback(
    (item, field) => {
      if (field === "from") {
        setSelectedFrom(item)
        setSelectedTo(null)
        setIsSearchingTo(false)
        // Сбрасываем запомненный запрос для "to" т.к. from изменился
        lastQueryRef.current.to = ""
        triggerDefaultTo({
          from_type: item.type,
          from_id: item.id,
        })
      } else {
        setSelectedTo(item)
      }
    },
    [triggerDefaultTo]
  )

  const swapDestinations = useCallback(() => {
    setSelectedFrom(selectedTo)
    setSelectedTo(selectedFrom)
    // Сбрасываем запомненные запросы при свапе
    lastQueryRef.current = { from: "", to: "" }
    setIsSearchingFrom(false)
    setIsSearchingTo(false)

    if (selectedTo) {
      triggerDefaultTo({
        from_type: selectedTo.type,
        from_id: selectedTo.id,
      })
    }
  }, [selectedFrom, selectedTo, triggerDefaultTo])

  const clearDestination = useCallback((field) => {
    if (field === "from") {
      setSelectedFrom(null)
      setSelectedTo(null)
      setIsSearchingTo(false)
      lastQueryRef.current = { from: "", to: "" }
    } else {
      setSelectedTo(null)
      lastQueryRef.current.to = ""
    }
  }, [])

  return {
    departureTerminals,
    arrivalTerminals,
    isLoading: isLoadingDefaultFrom || isLoadingSearchFrom,
    isLoadingTo: isLoadingDefaultTo || isLoadingSearchTo,
    selectedFrom,
    selectedTo,
    fetchDefault,
    fetchSearch,
    selectDestination,
    swapDestinations,
    clearDestination,
  }
}

export default useTransferDestinations
